// Canonical RBAC roles + permission map. Frontend mirror of the backend source
// of truth in portal-api-node/app/config/roles.js. Keep the two in sync.
//
// Roles are per-agency (tenant = ClientId); there is no cross-tenant role.
//   owner        - full control incl. billing + user management
//   office_admin - manage all office data + users; NO billing
//   agent_open   - reads + edits ALL records office-wide
//   agent_hybrid - reads all records; edits own records only (default)
//   agent_closed - reads only own contacts; edits own records only

export type Role = "owner" | "office_admin" | "agent_open" | "agent_hybrid" | "agent_closed";

export const ROLES: Record<string, Role> = {
  OWNER: "owner",
  OFFICE_ADMIN: "office_admin",
  AGENT_OPEN: "agent_open",
  AGENT_HYBRID: "agent_hybrid",
  AGENT_CLOSED: "agent_closed",
};

/** Roles that may manage any record in the office (bypass per-agent ownership). */
export const ADMIN_ROLES: Role[] = ["owner", "office_admin"];

/** All agent-type roles (non-admin staff). */
export const AGENT_ROLES: Role[] = ["agent_open", "agent_hybrid", "agent_closed"];

/**
 * Roles that READ office-wide. Wider than ADMIN_ROLES: agent_open is defined as
 * "view and edit ALL records", and the backend already grants it office-wide
 * reads (ownership.shouldScopeReads only ever restricts agent_closed). Use this
 * — not isAdminRole — to decide agency-vs-personal SCOPE on dashboards, reports
 * and calendars, or the UI ends up stricter than the API it calls.
 */
export const OFFICE_WIDE_READ_ROLES: Role[] = ["owner", "office_admin", "agent_open"];

/**
 * Roles whose restrictions are meaningless without a linked listing agent.
 *
 * Ownership is recorded against the AGENT id, so `shouldScopeReads` and
 * `agentOwns` both require req.agentId. Give someone agent_hybrid/agent_closed
 * with no link and the restriction silently does nothing on reads while blocking
 * their edits — the worst of both. owner/office_admin bypass ownership entirely
 * and agent_open is explicitly allow-all, so all three are fine unlinked.
 */
export const ROLES_REQUIRING_AGENT_LINK: Role[] = ["agent_hybrid", "agent_closed"];

/** Does this role need a linked listing agent to behave as described? */
export function requiresAgentLink(role?: string | null): boolean {
  return ROLES_REQUIRING_AGENT_LINK.includes(normalizeRole(role));
}

/** Does this role read agency-wide (as opposed to its own book)? */
export function readsOfficeWide(role?: string | null): boolean {
  return OFFICE_WIDE_READ_ROLES.includes(normalizeRole(role));
}

// Capability globs per role. Format `area.action`, matched by the glob matcher
// in portal/src/utils/permissions.ts. `owner` is the explicit union of every
// area (incl. billing) because that matcher only treats `*.`-suffixed entries
// as wildcards.
const AREA_GLOBS = [
  "listings.*",
  "contacts.*",
  "campaigns.*",
  "tasks.*",
  "appraisals.*",
  "inspections.*",
  "admin.*",
  "users.manage",
];

export const ROLE_PERMISSIONS: Record<Role, string[]> = {
  owner: ["billing.*", ...AREA_GLOBS],
  office_admin: [...AREA_GLOBS],
  agent_open: [
    "listings.read",
    "listings.edit.any",
    "contacts.read",
    "contacts.edit.any",
    "campaigns.own",
    "tasks.own",
    "appraisals.own",
    "inspections.own",
  ],
  agent_hybrid: [
    "listings.read",
    "listings.edit.own",
    "contacts.read",
    "contacts.edit.own",
    "campaigns.own",
    "tasks.own",
    "appraisals.own",
    "inspections.own",
  ],
  agent_closed: [
    "listings.read",
    "listings.edit.own",
    "contacts.read.own",
    "contacts.edit.own",
    "campaigns.own",
    "tasks.own",
    "appraisals.own",
    "inspections.own",
  ],
};

/**
 * Normalise a stored/legacy role string to a canonical role. The bare "agent"
 * value (legacy) maps to agent_hybrid. Unknown/missing values also default to
 * agent_hybrid so nothing is silently elevated.
 */
export function normalizeRole(role?: string | null): Role {
  switch ((role || "").toLowerCase()) {
    case "owner":
      return "owner";
    case "office_admin":
    case "admin":
    case "moderator":
      return "office_admin";
    case "agent_open":
      return "agent_open";
    case "agent_closed":
      return "agent_closed";
    case "agent_hybrid":
    case "agent":
    default:
      return "agent_hybrid";
  }
}

export function isAdminRole(role?: string | null): boolean {
  return ADMIN_ROLES.includes(normalizeRole(role));
}

/** Expand a role to the glob permission list fed into userStore.permissions. */
export function expandRole(role?: string | null): string[] {
  return ROLE_PERMISSIONS[normalizeRole(role)];
}
